import React from 'react';
import { useNavigate } from 'react-router-dom';

// Table: [Reviewed] | Title | Issue/Article Date | Review →
//
// The whole row navigates to the detail page, not just "Review" — the row is a
// wide target and people click the title. "Review →" stays as the visible
// affordance, and the row is a real <a>-equivalent for keyboard users.
//
// The Reviewed column is a per-user read marker, populated from `row.viewed`
// (server-side, from alert_views / hot_scam_views - hot scams track it too
// since 10-06). Hot Scams rows label their date "Article Date" instead of
// "Issue Date". The PSA number column was dropped in the 9-22 round (it still shows
// on the detail page), and the New / Current pair on 10-07: the tab dot and the
// row tint already say which alerts are new, and the title gets the width.
const AlertsTable = ({ rows, loading, error, agencyTab = true, showViewed = true }) => {
  const navigate = useNavigate();

  if (loading) {
    return <p className="py-12 text-center text-gray-400">Loading alerts&hellip;</p>;
  }

  if (error) {
    return <p className="py-12 text-center text-gray-500">Couldn&rsquo;t load alerts. Try again shortly.</p>;
  }

  if (!rows.length) {
    return <p className="py-12 text-center text-gray-500">No alerts published yet.</p>;
  }

  const open = (to) => navigate(to);

  return (
    // The table has a hard minimum width; give it its own horizontal scroller
    // so a narrow viewport scrolls the table rather than the whole hub.
    <div className="w-full overflow-x-auto">
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-gray-400">
          {showViewed && (
            <th className="py-2 pr-4 font-normal text-black text-center w-24" title="Reports you have opened">
              Reviewed
            </th>
          )}
          <th className="py-2 pr-4 font-normal text-black w-full min-w-[180px]">Title</th>
          <th className="py-2 px-2 font-normal xl:px-4 text-black whitespace-nowrap">
            {agencyTab ? 'Issue Date' : 'Article Date'}
          </th>
          <th className="py-2 pl-4" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.id}
            onClick={() => open(row.to)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open(row.to);
              }
            }}
            tabIndex={0}
            role="link"
            aria-label={`Open ${row.title}`}
            className={`border-b border-gray-300 align-top cursor-pointer transition-colors
              hover:bg-[#D6E7F4] focus:bg-[#D6E7F4] focus:outline-none focus:ring-1 focus:ring-blue-400
              ${row.isNew ? 'bg-[#E4F0F8]' : ''}`}
          >
            {showViewed && (
              <td className="py-3 pr-4 text-center">
                {/* √ once opened, blank until then (9-22 mockups) */}
                <span
                  className="text-black"
                  title={row.viewed ? 'You have opened this report' : 'Not opened yet'}
                  aria-label={row.viewed ? 'Reviewed' : 'Not reviewed'}
                >
                  {row.viewed ? '√' : ''}
                </span>
              </td>
            )}
            <td className="py-3 pr-4 text-black">{row.title}</td>
            <td className="py-3 px-2 whitespace-nowrap xl:px-4 text-black">{row.dateLabel}</td>
            <td className="py-3 pl-4 text-right">
              <span className="text-black whitespace-nowrap">Review &rarr;</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
};

export default AlertsTable;
